import type {
  CustomField,
  CustomFieldValue,
  DataElement,
  DataElementValue
} from '@/types';

export const toDataElement = (field: CustomField): DataElement => {
  const baseField = {
    id: field.id,
    displayOrder: field.display_order,
    required: field.required,
    key: field.key,
    entityType: field.entity_type,
    createdAt: field.created_at,
    updatedAt: field.updated_at,
    deleted: field.deleted
  };
  const baseFieldStructure = {
    label: field.field_structure.label,
    placeholder: field.field_structure.placeholder,
    localizedDisplay: field.field_structure.localized_display,
    localizedUom: field.field_structure.localized_uom
  };
  switch (field.element_type) {
    case 'NUMERIC':
      return {
        ...baseField,
        elementType: field.element_type,
        fieldStructure: {
          ...baseFieldStructure,
          defaultValue: field.field_structure.default_value,
          decimal: field.field_structure.decimal,
          minValue: field.field_structure.min_value,
          maxValue: field.field_structure.max_value
        }
      };
    case 'TEXT':
      return {
        ...baseField,
        elementType: field.element_type,
        fieldStructure: {
          ...baseFieldStructure,
          defaultValue: field.field_structure.default_value,
          lines: field.field_structure.lines,
          mask: field.field_structure.mask
        }
      };
    case 'SELECT':
      return {
        ...baseField,
        elementType: field.element_type,
        fieldStructure: {
          ...baseFieldStructure,
          defaultValue: field.field_structure.default_value,
          multiselect: field.field_structure.multiselect,
          options: field.field_structure.options.map(opt => ({
            value: opt.value,
            isDefaultValue: opt.is_default_value,
            archived: opt.archived,
            localizedDisplayOption: opt.localized_display_option
          }))
        }
      };
    case 'BOOLEAN':
      return {
        ...baseField,
        elementType: field.element_type,
        fieldStructure: {
          ...baseFieldStructure,
          defaultValue: field.field_structure.default_value,
          localizedDisplayValue: {
            trueLabel: field.field_structure.localized_display_value
              ?.true_label ?? { en: 'Yes' },
            falseLabel: field.field_structure.localized_display_value
              ?.false_label ?? { en: 'No' }
          }
        }
      };
    default:
      throw new Error('Invalid element type');
  }
};

export const toDataElementValue = (
  fieldValue: CustomFieldValue
): DataElementValue => {
  return {
    id: fieldValue.id,
    key: fieldValue.key,
    valueId: fieldValue.value_id,
    value: fieldValue.value
  };
};

export const toDataElementFormValue = (
  fieldValue: CustomFieldValue
): Record<string, unknown> => {
  return {
    [fieldValue.key]: fieldValue.value
  };
};

/** The part of a custom-field definition that decides its wire value. */
export type CustomFieldDefinition = Pick<DataElement, 'key' | 'elementType'>;

/**
 * A NUMERIC custom field has no `''`: an empty value — never set (`undefined`),
 * cleared (`InputNumber` holds `null`) or a legacy blank — goes out as `null`.
 */
const toNumericCustomFieldValue = (value: unknown): unknown =>
  value === undefined || value === '' ? null : value;

/**
 * Builds the `custom_fields` request list from the dynamic form's values.
 *
 * The element type decides how an empty value is sent: a NUMERIC field sends
 * `null`, every other type sends `''`. A key without a matching definition is
 * treated as non-numeric, so the definitions must cover every NUMERIC field
 * the values may hold.
 */
export const toCustomFieldValueList = (
  dataElements: Record<string, unknown> | undefined,
  definitions: readonly CustomFieldDefinition[]
): { key: string; value: unknown }[] => {
  const numericKeys = new Set(
    definitions
      .filter(definition => definition.elementType === 'NUMERIC')
      .map(definition => definition.key)
  );

  return Object.entries(dataElements ?? {}).map(([key, value]) => ({
    key,
    value: numericKeys.has(key)
      ? toNumericCustomFieldValue(value)
      : (value ?? '')
  }));
};
